import { FC, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import Button from '../../common/button';
import { IPsyBlock } from '../new-questions/new-question-psy';
import { IPsySetting } from '../../../store/psyTools.slice';
import { shuffleArray } from '../../../utils/textHelpers';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { clearMacClientSelections, clearMacSharedView } from '../../../store/mac.slice';

import { ShuffleIcon } from '../../icons/shuffle';

import { InlineLoader } from '../../common/inline-loader';

import { MacCardPrompt } from './mac-card-prompt';
import { MacCardsSelected } from './mac-card-selected';
import { MacDeckGalleryModal } from './mac-deck-gallery-modal';

const makeUrl = (url: string) => {
  if (!url) return '';

  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}${url}`;
};

export interface IMacCardsProps extends IPsyBlock {
  onCardZoom?: (zoomed: boolean) => void;
  /** Отправка колоды всем клиентам через data channel (LiveKit) */
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
}
type DeckSettingEntry = {
  value?: string[];
};

export interface IDeckPreviewProps {
  deck: IPsySetting;
  isSelected: boolean;
  coverDown: boolean;
  onSelect: (id: number) => void;
  onInspect: (deck: IPsySetting) => void;
}

export const DeckPreview: FC<IDeckPreviewProps> = ({ deck, isSelected, coverDown, onSelect, onInspect }) => {
  const primarySettings = Array.isArray(deck.settings) ? (deck.settings[0] as DeckSettingEntry | undefined) : undefined;
  const deckSettings = Array.isArray(primarySettings?.value) ? (primarySettings?.value as string[]) : [];
  const coverImage = deckSettings?.[0] || '';
  const borderColor = deckSettings?.[1] || 'transparent';
  const deckValues = Array.isArray(deck.value) ? deck.value : [];
  const faceImage = deckValues?.[0] || '';
  const coverSrc = coverImage ? makeUrl(coverImage) : '';
  const faceSrc = faceImage ? makeUrl(faceImage) : '';

  const [coverLoaded, setCoverLoaded] = useState(!coverSrc);
  const [faceLoaded, setFaceLoaded] = useState(!faceSrc);
  const [isFlipping, setIsFlipping] = useState(false);

  useEffect(() => {
    setCoverLoaded(!coverSrc);
  }, [coverSrc]);

  useEffect(() => {
    setFaceLoaded(!faceSrc);
  }, [faceSrc]);

  useEffect(() => {
    setIsFlipping(true);
    const timer = setTimeout(() => setIsFlipping(false), 700);

    return () => clearTimeout(timer);
  }, [coverDown]);

  const showSpinner = coverDown ? !!faceSrc && !faceLoaded : !!coverSrc && !coverLoaded;
  const transformValue = `${coverDown ? 'rotateY(180deg)' : 'rotateY(0deg)'} ${isFlipping ? 'scale(1.03)' : 'scale(1)'}`;

  const defaultBorderColor = '#6A6B70';

  return (
    <div className="flex w-full min-w-0 flex-col items-stretch gap-[5px] lg:gap-[10px] hover:cursor-pointer">
      <div
        style={{
          borderWidth: '2px',
          borderColor: isSelected ? borderColor : defaultBorderColor,
        }}
        className={classNames(
          'w-full aspect-[110/140] rounded-[14px] relative overflow-hidden flex items-center justify-center text-white border-2 border-[#6A6B70] bg-transparent',
          {
            '!bg-none !border-2 !border-[#6A6B70]': coverDown && !isSelected,
          },
        )}
        onClick={() => onSelect(deck.id)}
      >
        <button
          type="button"
          className="absolute top-1 right-1 z-30 flex h-6 w-6 items-center justify-center rounded-full bg-[rgba(0,0,0,0.1)] text-white text-sm font-semibold hover:bg-[rgba(0,0,0,0.4)] transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onInspect(deck);
          }}
          title="Просмотреть все карты колоды"
        >
          ?
        </button>
        <div style={{ perspective: '1200px' }} className="relative w-full h-full">
          <div
            className="relative w-full h-full transition-transform duration-700"
            style={{
              transformStyle: 'preserve-3d',
              transform: transformValue,
              transitionTimingFunction: 'cubic-bezier(0.4, 0.2, 0.2, 1)',
            }}
          >
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                backfaceVisibility: 'hidden',
              }}
            >
              {coverSrc ? (
                <img
                  className="object-cover w-full h-full"
                  src={coverSrc}
                  alt={`${deck.name}-cover`}
                  onLoad={() => setCoverLoaded(true)}
                  onError={() => setCoverLoaded(true)}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-[rgba(0,0,0,0.65)] px-2 text-center">
                  <p className="font-manrope uppercase text-lg font-extrabold">{deck.name}</p>
                </div>
              )}
            </div>

            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: 'rotateY(180deg)',
                backfaceVisibility: 'hidden',
              }}
            >
              {faceSrc ? (
                <img
                  className="object-cover w-full h-full"
                  src={faceSrc}
                  alt={`${deck.name}-face`}
                  onLoad={() => setFaceLoaded(true)}
                  onError={() => setFaceLoaded(true)}
                />
              ) : (
                <div className="px-2 text-center text-xs uppercase font-semibold tracking-wide">
                  Нет изображения лицевой стороны
                </div>
              )}
            </div>
          </div>
        </div>

        {showSpinner && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 15 }}
          >
            <InlineLoader variant="small" />
          </div>
        )}

        {coverDown && (
          <div
            className="absolute bottom-0 left-0 w-full text-white text-[9px] leading-tight text-center select-none"
            style={{
              background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.45) 45%, rgba(0, 0, 0, 0.85) 100%)',
              minHeight: '32px',
              padding: '6px 6px 4px',
              zIndex: 20,
            }}
            title={deck.name.length > 16 ? deck.name : undefined}
          >
            <span
              className="block w-full overflow-hidden text-ellipsis whitespace-normal line-clamp-2 break-words"
              style={{ maxWidth: '100%' }}
              title={deck.name.length > 16 ? deck.name : undefined}
            >
              {deck.name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const MacCardsPsy: FC<IMacCardsProps> = ({
  setResult,
  onCardZoom,
  onToolActivated,
  sendMacCardsOpen,
}) => {
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);
  const selectedCards = useAppSelector((state) => state.macStateSlice.selected);
  const clientSelectionsByParticipant = useAppSelector((state) => state.macStateSlice.clientSelectionsByParticipant);

  const [selectedCard, setSelectedCard] = useState<null | number>(null);
  const [coverDown, setCoverDown] = useState(false);
  const [inspectedDeck, setInspectedDeck] = useState<IPsySetting | null>(null);
  const [activeClientTab, setActiveClientTab] = useState<string | null>(null);
  const lastSavedCardsSignatureRef = useRef<string | null>(null);
  const clientParticipantIds = Object.keys(clientSelectionsByParticipant);
  const cards = psyTools.find((el) => el.name === 'metaforic_cards')?.psy_tool_settings || [];

  const arr = cards;
  const activateTool = () => {
    const selectedSettings = cards.find((el) => el.id === selectedCard)?.settings?.[0]?.value || [];
    const cover = selectedSettings[0] || '';
    const color = selectedSettings[1] || '';
    const selectedDeck = cards.find((el) => el.id === selectedCard);
    const deck = selectedDeck?.value || [];
    const payload = {
      show: coverDown,
      cover,
      color,
      result: {
        name: selectedDeck?.name || '',
        value: shuffleArray(Object.values(deck)),
      },
    };

    dispatch(clearMacClientSelections());
    dispatch(clearMacSharedView());
    sendMacCardsOpen?.(payload);

    onToolActivated?.('metaforic_cards', {
      deck_id: selectedDeck?.id ?? null,
      deck_name: selectedDeck?.name ?? null,
      cover_mode: coverDown ? 'face_up' : 'face_down',
    });
    dispatch(setActiveTool(PsyToolsId.MAC_CARDS));
    // onSetMenu(0);
    // if (selectedCard) {
    //   setShowCards && setShowCards(coverUp);
    // }
  };

  useEffect(() => {
    if (selectedCards.length === 0) {
      lastSavedCardsSignatureRef.current = null;
      return;
    }

    const selectedCardImages = selectedCards.map((el: { img: string }) => el.img);
    const signature = JSON.stringify(selectedCardImages);
    if (lastSavedCardsSignatureRef.current === signature) {
      return;
    }

    lastSavedCardsSignatureRef.current = signature;
    const data = {
      date_time: new Date().toISOString(),
      cards: selectedCardImages,
      comment: '',
    };

    setResult((prev) => {
      return { ...prev, mac_cards: [...prev.mac_cards, data] };
    });
  }, [selectedCards, setResult]);

  if (activeTool === PsyToolsId.MAC_CARDS) {
    if (selectedCards.length > 0) {
      return (
        <div
          className={classNames('h-full w-full flex flex-row pr-[0.25rem]', {
            'lg:min-w-[600px] md:min-w-[400px]': selectedCards.length <= 0,
          })}
        >
          <MacCardsSelected selectedCards={selectedCards} onCardZoom={onCardZoom} />
        </div>
      );
    }
    if (clientParticipantIds.length > 0) {
      const activeId =
        activeClientTab && clientParticipantIds.includes(activeClientTab) ? activeClientTab : clientParticipantIds[0];

      return (
        <div className="min-w-[300px] h-full flex flex-col bg-grey1 rounded-[15px] p-4 overflow-hidden">
          <h3 className="font-comfortaa font-bold text-lg text-white mb-1">МАК-карты</h3>
          <p className="text-white text-sm font-comfortaa mb-3">
            Клиенты сделали свой выбор. Выберите вкладку, чтобы посмотреть результат каждого.
          </p>
          <div className="flex flex-wrap gap-2 border-b border-[rgba(255,255,255,0.2)] pb-2 mb-3 shrink-0">
            {clientParticipantIds.map((participantId, idx) => {
              const isActive = participantId === activeId;

              return (
                <button
                  key={participantId}
                  type="button"
                  onClick={() => setActiveClientTab(participantId)}
                  className={classNames(
                    'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium text-white transition-colors',
                    isActive ? 'bg-[#000000]' : 'bg-[#000000]/60 hover:bg-[#000000]/80',
                  )}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {activeId && clientSelectionsByParticipant[activeId]?.length > 0 ? (
              <MacCardsSelected selectedCards={clientSelectionsByParticipant[activeId]} onCardZoom={onCardZoom} />
            ) : (
              <p className="text-white/70 text-sm">Нет выбранных карт</p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="sm:w-[30vw] lg:w-[15vw]  min-w-[300px] h-full flex flex-col ">
        <MacCardPrompt coverUp={coverDown} />
      </div>
    );
  }

  return (
    <div className="w-full min-w-[300px] lg:w-[15vw] lg:min-w-[500px] h-full flex flex-col">
      <p className="ml-[1rem] mt-[1rem] text-base text-white pb-[1rem]">
        Выберите колоду для показа клиенту и способ ее отображения: рубашкой вверх или вниз.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[30px] lg:gap-[12px] max-h-[80vh] overflow-y-auto pr-4 pl-4 white-scroll">
        {arr &&
          arr.map((el, idx) => (
            <DeckPreview
              key={el?.id ?? idx}
              deck={el}
              coverDown={coverDown}
              isSelected={selectedCard === el?.id}
              onSelect={(id) => setSelectedCard(id)}
              onInspect={(deck) => setInspectedDeck(deck)}
            />
          ))}
      </div>

      <div className="mt-auto flex gap-[10px] pt-4">
        <Button
          className={classNames(
            '!w-auto p-[5px] flex items-center justify-center border-transparent hover:border-blackBase hover:bg-grey3',
            {
              '!bg-[#DEC05E] hover:!bg-[#DEC05E] !border-[#DEC05E]': coverDown,
            },
          )}
          type="grey"
          onClick={() => setCoverDown(!coverDown)}
        >
          <ShuffleIcon />
        </Button>
        <Button
          className="!text-sm xl:!text-base p-[6px_10px] !self-center"
          disabled={!selectedCard}
          onClick={() => {
            activateTool();
          }}
        >
          Показать колоду
        </Button>
      </div>
      <MacDeckGalleryModal
        isOpen={!!inspectedDeck}
        deckName={inspectedDeck?.name}
        cards={Array.isArray(inspectedDeck?.value) ? (inspectedDeck?.value as string[]) : []}
        onClose={() => setInspectedDeck(null)}
      />
    </div>
  );
};
